'use client';

/* Lifecycle section — interactive 4-stage stepper with auto-advance. */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../icon';
import { SectionHead, toneVar, useReveal } from '../primitives';
import { lifecycle, href } from '../data';

export function LifecycleSection() {
  useReveal();
  const stages = lifecycle;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (paused) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setI(v => (v + 1) % stages.length), 4200);
    return () => clearInterval(t);
  }, [paused, stages.length]);
  const s = stages[i];
  if (!s) return null;
  const c = toneVar(s.tone);

  return (
    <section className="section" id="lifecycle">
      <div className="container">
        <SectionHead
          eyebrow="The dispute lifecycle"
          title="Four stages. One platform. Every chance to resolve."
          lead="Each service is separate and you choose what to run — but they’re built to work in sequence, creating multiple opportunities to settle a concern before it ever becomes a costly chargeback."
        />

        {/* stepper rail */}
        <div className="reveal" style={{ marginTop: 44 }} ref={wrapRef}
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="life-rail" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, position: 'relative' }}>
            <div style={{ position: 'absolute', top: 23, left: '12.5%', right: '12.5%', height: 2, background: 'var(--line)' }} />
            <div style={{ position: 'absolute', top: 23, left: '12.5%', height: 2, background: `linear-gradient(90deg, var(--cyan), ${c})`,
              width: `calc(${(i / (stages.length - 1)) * 75}% )`, transition: 'width .6s cubic-bezier(.2,.7,.3,1)', borderRadius: 2 }} />
            {stages.map((st, idx) => {
              const sc = toneVar(st.tone); const on = idx === i; const done = idx < i;
              return (
                <button key={st.key} onClick={() => setI(idx)} className="life-step"
                  style={{ background: 'none', border: 'none', padding: '0 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
                  <span style={{ width: 48, height: 48, borderRadius: 14, display: 'grid', placeItems: 'center',
                    color: on || done ? sc : 'var(--fg-subtle)',
                    background: on ? `color-mix(in oklch, ${sc} 22%, var(--bg))` : 'var(--bg-1)',
                    border: `1px solid ${on || done ? sc : 'var(--line)'}`,
                    boxShadow: on ? `0 0 0 6px color-mix(in oklch, ${sc} 12%, transparent)` : 'none',
                    transition: 'all .4s' }}>
                    <Icon name={done ? 'check' : st.icon} size={21} strokeWidth={1.9} />
                  </span>
                  <span className="stack" style={{ gap: 3, alignItems: 'center' }}>
                    <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>0{idx + 1}</span>
                    <span style={{ fontSize: 15, fontWeight: 550, color: on ? 'var(--fg)' : 'var(--fg-muted)' }}>{st.label}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* detail panel */}
          <div className="card life-panel" key={i} style={{ marginTop: 30, padding: 'clamp(24px, 3.6vw, 44px)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(600px 280px at 88% -10%, color-mix(in oklch, ${c} 18%, transparent), transparent 62%)`, pointerEvents: 'none' }} />
            <div className="life-panel-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: 'clamp(28px, 4vw, 56px)', alignItems: 'center', position: 'relative' }}>
              <div className="stack" style={{ gap: 18 }}>
                <div className="row wrap" style={{ gap: 10 }}>
                  <span className="tag" style={{ color: c, borderColor: `color-mix(in oklch, ${c} 40%, transparent)`, background: `color-mix(in oklch, ${c} 12%, transparent)` }}>{s.kicker}</span>
                  {s.isNew && <span className="tag tag-green">Agentic · New</span>}
                </div>
                <h3 className="h3 balance" style={{ margin: 0 }}>{s.title}</h3>
                <p className="lead pretty" style={{ margin: 0, fontSize: 17 }}>{s.blurb}</p>
                <div className="stack" style={{ gap: 11, marginTop: 4 }}>
                  {s.points.map((p, k) => (
                    <div key={k} className="row" style={{ gap: 11, alignItems: 'flex-start' }}>
                      <Icon name="circle-check" size={19} style={{ color: c, marginTop: 1, flexShrink: 0 }} />
                      <span style={{ fontSize: 15.5, color: 'var(--fg-soft)' }}>{p}</span>
                    </div>
                  ))}
                </div>
                {s.isNew && <a href={href('engine')} className="link-arrow" style={{ marginTop: 4 }}>Explore the Representment Engine<Icon name="arrow-right" size={16} /></a>}
              </div>
              <div className="stack" style={{ gap: 16 }}>
                <div style={{ display: 'grid', placeItems: 'center', aspectRatio: '1', borderRadius: 'var(--r-lg)', background: `radial-gradient(circle at 50% 42%, color-mix(in oklch, ${c} 18%, transparent), transparent 60%)`, border: '1px solid var(--line-soft)', position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: 0, borderRadius: 'inherit', overflow: 'hidden' }}><div className="grid-bg" style={{ opacity: 0.4 }} /></div>
                  <div style={{ width: 96, height: 96, borderRadius: 26, display: 'grid', placeItems: 'center', color: c, background: `color-mix(in oklch, ${c} 16%, var(--bg-1))`, border: `1px solid color-mix(in oklch, ${c} 40%, transparent)`, boxShadow: `0 0 60px color-mix(in oklch, ${c} 30%, transparent)`, position: 'relative' }}>
                    <Icon name={s.icon} size={42} strokeWidth={1.6} />
                  </div>
                </div>
                <div className="card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'baseline', gap: 12, background: 'var(--bg-sunken)' }}>
                  <span className="stat-fig" style={{ fontSize: 38, color: c }}>{s.stat.fig}</span>
                  <span className="muted pretty" style={{ fontSize: 13.5 }}>{s.stat.label}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
